<script setup lang="ts">
import {onMounted, useTemplateRef} from "vue";
import {ICellEditorParams} from "ag-grid-community";
import {type QBtnDropdown, QEditor, type QList} from "quasar";
import BigTextFieldWidgetUtil from "@/utils/editView/widgets/BigTextFieldWidgetUtil.ts";
import EditScreenGridHandler from "@/utils/common/EditScreenGridHandler.ts";
import {onClickOutside} from "@vueuse/core";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import Translator from "@/utils/common/Translator.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import RichTextColorButton from "@/components/inputs/RichTextColorButton.vue";
import EditScreenGridColumn = namaTypes.EditScreenGridColumn;
import FieldMetaData = namaTypes.FieldMetaData;

const props = defineProps<{
  params: ICellEditorParams & {
    colId: EditScreenGridColumn["id"],
    columnMetaData: FieldMetaData,
    handler: () => EditScreenGridHandler,
    lang: { value: string }
  }
}>();

const qGridEditorRef = useTemplateRef<QEditor>("qGridEditorRef");
const qListRef = useTemplateRef<QList>("qListRef");
const handler = props.params.handler();
const columnMetaData = props.params.columnMetaData
const fieldUtil = new BigTextFieldWidgetUtil(handler.presenter, columnMetaData, props.params);
const colId = props.params.colId || "";

onClickOutside(qGridEditorRef, () => props.params.stopEditing(), {
  ignore: [qListRef, '.nama-richtext-color-menu']
})

const isColumnDisabled = (): boolean => {
  return handler.presenter.isColumnDisabled(props.params.data.id, handler.gridId, colId)
}

if (props.params?.eventKey?.length === 1)
  fieldUtil.inputFieldValueRef.value = props.params.eventKey;
const editorFonts = {
  arial: 'Arial',
  arial_black: 'Arial Black',
  comic_sans: 'Comic Sans MS',
  courier_new: 'Courier New',
  impact: 'Impact',
  lucida_grande: 'Lucida Grande',
  times_new_roman: 'Times New Roman',
  verdana: 'Verdana'
};

function getValue() {
  return fieldUtil.inputFieldValueRef.value;
}

onMounted(() => {
  if (qGridEditorRef.value) {
    setTimeout(() => {
      qGridEditorRef.value!.focus();
      if (props.params?.eventKey?.length === 1)
        return;
      const editor = qGridEditorRef.value!.$el.querySelector('.q-editor__content');
      if (editor) {
        const range = document.createRange();
        range.selectNodeContents(editor);
        const selection = window.getSelection()!;
        selection.removeAllRanges();
        selection.addRange(range);
      }
    })
  }
});

function namaUpdateValue(value: string) {
  fieldUtil.inputFieldValueRef.value = value;
}

function onKeyDown(event: KeyboardEvent) {
  if (AGGridUtils.isBulkAction(event)) {
    namaUpdateValue(getValue());
    AGGridUtils.dispatchBulkEventFor(qGridEditorRef);
  } else if (event.key === 'F2') {
    props.params?.api.stopEditing();
  }
}

defineExpose({getValue, namaUpdateValue});
</script>

<template>
  <form autocomplete="off">
    <q-editor
      class="tw-rounded-lg dark:tw-bg-namaBackground-deep-dark" @keydown="onKeyDown"
      content-class="dark:tw-border-white tw-relative" min-height="5rem"
      v-model="fieldUtil.inputFieldValueRef.value"
      ref="qGridEditorRef"
      :toolbar="[
                  ['speech'],
                  [
                    {
                      label: $q.lang.editor.align,
                      icon: $q.iconSet.editor.align,
                      fixedLabel: true,
                      list: 'only-icons',
                      options: ['left', 'center', 'right', 'justify']
                    }
                  ],
                  ['bold', 'italic', 'strike', 'underline', 'subscript', 'superscript', 'fontColor'],
                  ['token', 'hr', 'link', 'custom_btn'],
                  ['fullscreen'],
                  [
                    {
                      label: $q.lang.editor.formatting,
                      icon: $q.iconSet.editor.formatting,
                      list: 'no-icons',
                      options: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code']
                    },
                    {
                      label: $q.lang.editor.fontSize,
                      icon: $q.iconSet.editor.fontSize,
                      fixedLabel: true,
                      fixedIcon: true,
                      list: 'no-icons',
                      options: ['size-1', 'size-2', 'size-3', 'size-4', 'size-5', 'size-6', 'size-7']
                    },
                    {
                      label: $q.lang.editor.defaultFont,
                      icon: $q.iconSet.editor.font,
                      fixedIcon: true,
                      list: 'no-icons',
                      options: ['default_font', 'arial', 'arial_black', 'comic_sans', 'courier_new', 'impact', 'lucida_grande', 'times_new_roman', 'verdana']
                    },
                    'removeFormat'
                  ],
                  ['quote', 'unordered', 'ordered', 'outdent', 'indent'],
                  ['undo', 'redo']
                ]"
      :fonts="editorFonts">
      <template #fontColor>
        <RichTextColorButton :editor="qGridEditorRef" :disable="isColumnDisabled()"/>
      </template>
      <template #speech>
        <div class="tw-flex tw-items-center tw-gap-1 tw-p-1 tw-border-1 dark:tw-border-bg-white/10">
          <div
            @click.stop="fieldUtil.startSpeechRecognition()"
            class=" tw-cursor-pointer tw-p-1.5 tw-rounded-md dark:hover:tw-bg-white/10 hover:tw-bg-black/10">
            <NamaIcon
              :default-icon="fieldUtil.speech.isListening.value?'nm-wave':'nm-microphone'"
              class="tw-h-full tw-size-4"/>
          </div>
          <q-btn-dropdown
            color="transparent"
            size="sm" dense :disable="isColumnDisabled() || !fieldUtil.speech.isSupported.value"
            class="q-btn-dropdown-dense tw-h-fit"
            unelevated>
            <q-list ref="qListRef">
              <q-item
                dense :class="{'selected-list-item tw-text-xs':fieldUtil.lang.value==item.value}"
                @click="fieldUtil.onSpeechLanguageChange(item.value)"
                clickable
                v-close-popup
                v-for="item in BigTextFieldWidgetUtil.speechLanguages">
                <q-item-section>
                  <q-item-label v-text="Translator.translateId(item.value)"/>
                </q-item-section>
              </q-item>
            </q-list>
          </q-btn-dropdown>
        </div>
      </template>
    </q-editor>
  </form>
</template>
